import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isBoolean } from 'lodash';

import { ButtonCircle } from './Button';
import { ActionButton } from './ActionButton';
import { Icon } from './Icon';

import '../scss/_block.scss';
import compStyles from './collapsibleBlock/index.module.scss';

export const CollapsibleBlock = (props) => {
  const {
    children,
    header,
    collapsible,
    isCollapseOpen,
    onToggle,
    className = '',
    id,
    customStyle,
    titleIcons,
    shouldUnmount = false,
    persistSummary = false,
    actions = [],
  } = props;
  const [internalIsOpen, setInternalIsOpen] = useState(!collapsible);

  useEffect(() => {
    if (isCollapseOpen !== internalIsOpen) {
      setInternalIsOpen(isCollapseOpen);
    }
  }, [isCollapseOpen, internalIsOpen]);

  const getKeyIcon = (key) => {
    const keyIcon = header.keyIcon?.[key];
    return keyIcon ? <span className={`ml-1 mr-1 ${styles.keyIcon}`}>{keyIcon}</span> : null;
  };

  const getSummary = () =>
    header &&
    Object.keys(header.summary).map((key) =>
      header.summary[key].length ? (
        <span className={styles.summaryContainer} key={key}>
          <span className={styles.summaryLabel}>
            {key}
            {getKeyIcon(key)}:{' '}
          </span>
          {header.icon && <span>{header.icon[key]}</span>}
          <span>{header.summary[key]}</span>
        </span>
      ) : null,
    );

  const styles = useMemo(() => {
    if (customStyle) {
      return { ...compStyles, ...customStyle };
    }
    return compStyles;
  }, [customStyle]);

  const handleToggle = useCallback(() => {
    if (collapsible) {
      onToggle?.(!internalIsOpen, id);
      setInternalIsOpen(!internalIsOpen);
    }
  }, [collapsible, internalIsOpen, onToggle, id]);

  const shouldExpandBlock = useMemo(
    () =>
      collapsible
        ? (isBoolean(isCollapseOpen) && isCollapseOpen) ||
          (!isBoolean(isCollapseOpen) && internalIsOpen)
        : true,
    [internalIsOpen, isCollapseOpen, collapsible],
  );

  const { panelHeaderExpand = '', panelHeaderCollapse = '' } = styles;

  return (
    <div id={id} className={`block ${styles.block} ${className}`}>
      {header && (
        <div
          className={`${styles.blockHeader} ${
            shouldExpandBlock ? panelHeaderExpand : `${panelHeaderCollapse} mb-0`
          }`}
          onClick={handleToggle}
        >
          <div className={`block-title ${styles.blockTitle}`}>
            {header.id && <div className={styles.blockIndex}>{header.id}</div>}
            <h4 className={styles.blockTitleText}>{header.title}</h4>
            {header && (!shouldExpandBlock || persistSummary) && header.summary && (
              <div className={styles.summary}>{getSummary()}</div>
            )}
          </div>
          {titleIcons && shouldExpandBlock && (
            <ul className={styles.titleIconsWrapper}>
              {titleIcons.map((icon) => (
                <li
                  onClick={(e) => {
                    e.stopPropagation();
                    icon.onClick();
                  }}
                  key={icon.label}
                >
                  <Icon className={icon.className ? icon.className : ''} name={icon.iconName} />
                </li>
              ))}
            </ul>
          )}
          <div className={`actions ${styles.actionsWrapper}`}>
            {collapsible && (
              <div className={styles.arrow}>
                <ButtonCircle
                  onClick={handleToggle}
                  iconName={shouldExpandBlock ? 'Expand' : 'Right'}
                />
              </div>
            )}
            {actions.length > 0 && <ActionButton className={styles.actions} actions={actions} />}
          </div>
        </div>
      )}
      {shouldUnmount && shouldExpandBlock && <div>{children}</div>}
      {!shouldUnmount && <div hidden={!shouldExpandBlock}>{children}</div>}
    </div>
  );
};

CollapsibleBlock.propTypes = {
  collapsible: PropTypes.bool,
  isCollapseOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  isExtended: PropTypes.bool,
  header: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    summary: PropTypes.object,
    id: PropTypes.number,
    icon: PropTypes.object,
    keyIcon: PropTypes.element,
  }),
  id: PropTypes.string,
  customStyle: PropTypes.object,
  shouldUnmount: PropTypes.bool,
  persistSummary: PropTypes.bool,
  actions: PropTypes.array,
  titleIcons: PropTypes.array,
};
